import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Send, Mail, Phone, MapPin, Navigation, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";

const initialForm = { nome: "", email: "", messaggio: "" };

const ContactSection = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.email.trim() || !form.messaggio.trim()) {
      toast.error(t("contact.errorFill"));
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      toast.success(t("contact.success"));
      setForm(initialForm);
    } catch (err) {
      const msg = err?.response?.data?.detail || t("contact.errorGeneric");
      toast.error(typeof msg === "string" ? msg : t("contact.errorGeneric"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contatti"
      className="section-y"
      style={{ background: "#fbf5e9" }}
      data-testid="contact-section"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-start">
        {/* Left */}
        <div className="lg:col-span-2">
          <span
            className="font-serif-display uppercase tracking-[0.3em] text-xs"
            style={{ color: "#c5371f" }}
          >
            {t("contact.eyebrow")}
          </span>
          <h2
            className="font-serif-display font-bold mt-3"
            style={{ color: "#2a160c", fontSize: "clamp(1.9rem, 3vw, 2.6rem)", lineHeight: 1.15 }}
          >
            {t("contact.title")}
          </h2>
          <p
            className="mt-4 text-base leading-relaxed"
            style={{ color: "#3b2417" }}
            dangerouslySetInnerHTML={{ __html: t("contact.intro") }}
          />

          <div className="mt-8 space-y-4">
            <div className="flex items-start gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ background: "#c5371f" }}
              >
                <Mail size={18} color="#fff" />
              </div>
              <div>
                <p className="font-semibold" style={{ color: "#2a160c" }}>{t("contact.email")}</p>
                <p className="text-sm" style={{ color: "#5b3621" }}>caffecola1960@libero.it</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ background: "#c5371f" }}
              >
                <Phone size={18} color="#fff" />
              </div>
              <div>
                <p className="font-semibold" style={{ color: "#2a160c" }}>{t("contact.phone")}</p>
                <p className="text-sm" style={{ color: "#5b3621" }}>334 812 2013</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ background: "#c5371f" }}
              >
                <MapPin size={18} color="#fff" />
              </div>
              <div>
                <p className="font-semibold" style={{ color: "#2a160c" }}>{t("contact.address")}</p>
                <p className="text-sm" style={{ color: "#5b3621" }}>{t("contact.addressValue")}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={submit}
          className="lg:col-span-3 rounded-2xl p-6 sm:p-8 lg:p-10 shadow-xl"
          style={{ background: "#fff", border: "1px solid rgba(91,54,33,0.12)" }}
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: "#2a160c" }}>
                {t("contact.nome")} *
              </label>
              <input
                name="nome"
                value={form.nome}
                onChange={change}
                className="cc-input"
                placeholder={t("contact.nomePh")}
                required
                data-testid="contact-input-nome"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: "#2a160c" }}>
                {t("contact.emailLabel")} *
              </label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={change}
                className="cc-input"
                placeholder={t("contact.emailPh")}
                required
                data-testid="contact-input-email"
              />
            </div>
          </div>
          <div className="mt-5">
            <label className="block text-sm font-semibold mb-2" style={{ color: "#2a160c" }}>
              {t("contact.messaggio")} *
            </label>
            <textarea
              name="messaggio"
              value={form.messaggio}
              onChange={change}
              rows={6}
              className="cc-input resize-y"
              placeholder={t("contact.messaggioPh")}
              required
              data-testid="contact-input-messaggio"
            />
          </div>
          <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
            <p className="text-xs" style={{ color: "#7a4a2e" }}>
              {t("contact.privacyNote")}
            </p>
            <button
              type="submit"
              disabled={submitting}
              className="btn-red focus-ring inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold uppercase tracking-wider text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="contact-submit"
            >
              {submitting ? t("contact.submitting") : t("contact.submit")} <Send size={15} />
            </button>
          </div>
        </form>
      </div>

      {/* Google Maps embed */}
      <div className="max-w-6xl mx-auto px-6 lg:px-8 mt-16 lg:mt-20">
        <div className="text-center mb-6">
          <span className="font-serif-display uppercase tracking-[0.3em] text-xs" style={{ color: "#c5371f" }}>
            {t("contact.mapTitle")}
          </span>
          <p className="mt-2 text-sm sm:text-base max-w-2xl mx-auto" style={{ color: "#5b3621" }}>
            {t("contact.mapCaption")}
          </p>
        </div>
        <div
          className="relative rounded-3xl overflow-hidden shadow-xl"
          style={{ border: "1px solid rgba(91,54,33,0.15)", boxShadow: "0 30px 60px -30px rgba(42,22,12,0.45)" }}
          data-testid="contact-map"
        >
          <iframe
            title={t("contact.mapIframeTitle")}
            src="https://www.google.com/maps?q=Via%20Marciotti%2084%2F16%2C%20San%20Giuseppe%20Vesuviano%2C%20NA%2C%20Italy&hl=it&z=15&output=embed"
            width="100%"
            height="420"
            style={{ border: 0, display: "block", filter: "saturate(0.95)" }}
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
          />
          <div className="absolute top-4 left-4 rounded-full px-4 py-2 shadow-lg flex items-center gap-2" style={{ background: "#fbf5e9" }}>
            <MapPin size={14} style={{ color: "#c5371f" }} />
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#3b2417" }}>
              Caffè Cola 1960
            </span>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <a
            href="https://www.google.com/maps/dir/?api=1&destination=Via+Marciotti+84%2F16%2C+San+Giuseppe+Vesuviano+NA+Italy"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-red focus-ring inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold uppercase tracking-wider text-xs"
            data-testid="map-directions-btn"
          >
            <Navigation size={13} /> {t("contact.getDirections")}
          </a>
          <a
            href="https://www.google.com/maps/search/?api=1&query=Via+Marciotti+84%2F16%2C+San+Giuseppe+Vesuviano+NA+Italy"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-coffee focus-ring inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold uppercase tracking-wider text-xs"
            data-testid="map-open-btn"
          >
            <ExternalLink size={13} /> {t("contact.openMaps")}
          </a>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
